import { Link } from 'react-router-dom';
import { Check, ArrowRight, Handshake, Info } from 'lucide-react';
import { Button, Container, Eyebrow, Reveal, SectionHeading } from '@/components/ui';
import { CtaBand, PageHero } from '@/components/sections';
import { usePageTitle } from '@/components/layout/SiteLayout';
import {
  APPLIANCE_BASE_PRICE_USD,
  LICENSE_PRESETS,
  SOFTWARE_TIERS,
  ALL_LICENSE_PRICE_USD,
  HMI_LICENSE_PRICE_USD,
  HMI_SUPPORT_YEARLY_USD,
  CLOUD_PLANS,
  CLOUD_PLAN_CATEGORIES,
  LICENSABLE_FEATURES,
  LICENSE_FEATURE_PRICES_USD,
  MAINTENANCE_RATE,
  bundleListPrice,
  type CloudPlan,
} from '@scada-ink/shared/cloud';
import { formatUsd } from '@/components/LicenseBuilder';

const LICENSABLE_TOTAL = LICENSABLE_FEATURES.reduce((s, f) => s + LICENSE_FEATURE_PRICES_USD[f], 0);
import { MAX_PARTNER_DISCOUNT } from '@/content/training';
import { useSession } from '@/lib/session';
import { HMI_SITE_URL, orderLink } from '@/lib/site';

const CATEGORY_ANCHORS: Record<string, string> = { ai: 'ai', archive: 'archive', remote: 'remote', notifications: 'alerts', bundle: 'bundle' };

function PlanCard({ plan }: { plan: CloudPlan }) {
  const { session } = useSession();
  const subscribeTo = session ? `/portal/billing?plan=${plan.id}` : `/register?plan=${plan.id}`;
  const dark = plan.category === 'bundle';
  return (
    <div
      className={`relative h-full flex flex-col rounded-[var(--radius-glass)] p-6 border ${
        dark ? 'bg-navy-900 text-white border-navy-900' : plan.highlight ? 'bg-white border-brand-primary/40 shadow-lg shadow-brand-primary/10' : 'bg-white border-glass-border'
      }`}
    >
      {plan.highlight && !dark && (
        <span className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-[11px] font-semibold">Most popular</span>
      )}
      <h3 className="text-lg font-bold">{plan.name}</h3>
      <p className={`mt-1 text-sm ${dark ? 'text-slate-300' : 'text-text-secondary'}`}>{plan.tagline}</p>
      <div className="mt-5 flex items-baseline gap-1.5">
        <span className="text-4xl font-extrabold tracking-tight">${plan.priceMonthlyUsd}</span>
        <span className={`text-sm ${dark ? 'text-slate-400' : 'text-text-muted'}`}>/ {plan.unit === 'appliance' ? 'appliance / ' : ''}month</span>
      </div>
      {dark && (
        <div className="mt-1 text-xs text-brand-accent">
          ${bundleListPrice(plan)} if bought separately — save ${bundleListPrice(plan) - plan.priceMonthlyUsd}/month
        </div>
      )}
      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className={`flex items-start gap-2 text-sm ${dark ? 'text-slate-200' : 'text-text-secondary'}`}>
            <Check size={15} className={`mt-0.5 shrink-0 ${dark ? 'text-brand-accent' : 'text-status-running'}`} />
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-7">
        <Button to={subscribeTo} variant={dark ? 'light' : plan.highlight ? 'primary' : 'secondary'} className="w-full" arrow>
          Subscribe
        </Button>
      </div>
    </div>
  );
}

export function PricingPage() {
  usePageTitle('Pricing');
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Pricing' }]}
        eyebrow="Pricing"
        title="One system price. Pay once for what you add."
        intro={`A Scada.Ink system starts at ${formatUsd(APPLIANCE_BASE_PRICE_USD)} — the appliance with the full core platform. Add licensed features with a one-time, perpetual key, and subscribe to Scada.Ink Cloud services by the month.`}
        actions={
          <>
            <Button to="/license-builder" size="lg" arrow>
              Build your license
            </Button>
            <Button to="/pricing#cloud" size="lg" variant="secondary">
              Cloud plans
            </Button>
          </>
        }
      />

      {/* Software */}
      <section id="software" className="scroll-mt-32 py-20">
        <Container>
          <SectionHeading
            eyebrow="Software licenses"
            title="The core platform comes with every system"
            intro="Every system includes the appliance and the core platform: every PLC, CNC and robot driver, unlimited devices and tags, dashboards, the historian, scripts and workflows. Licensed features are a one-time, perpetual purchase locked to your appliance."
            action={
              <Button to="/license-builder" arrow>
                Open the License Builder
              </Button>
            }
          />
          <div className="grid lg:grid-cols-3 gap-5">
            {[
              {
                name: 'Base system',
                price: formatUsd(APPLIANCE_BASE_PRICE_USD),
                sub: 'appliance + core platform, one-time',
                body: 'The Scada.Ink appliance with all 25+ drivers, unlimited tags, dashboards, historian, alarms with email, scripts, workflows, custom pages, data tables, users, backups and paired appliances.',
                cta: 'Build your system',
                to: '/license-builder',
                highlight: false,
              },
              {
                name: 'Build your own',
                price: `From ${formatUsd(Math.min(...Object.values(LICENSE_FEATURE_PRICES_USD)))}`,
                sub: 'per feature, one-time, added to the base',
                body: 'Pick exactly the capabilities you need — OEE, reporting, ladder logic, MES and more — and see the price as you go. Upgrade any time.',
                cta: 'Choose features',
                to: '/license-builder',
                highlight: false,
              },
              {
                name: 'All features',
                price: formatUsd(ALL_LICENSE_PRICE_USD),
                sub: 'one-time, added to the base',
                body: `Every licensed capability on one appliance — ${formatUsd(LICENSABLE_TOTAL - ALL_LICENSE_PRICE_USD)} less than buying them separately. ${formatUsd(APPLIANCE_BASE_PRICE_USD + ALL_LICENSE_PRICE_USD)} as a complete new system.`,
                cta: 'Get All features',
                to: '/license-builder?all=1',
                highlight: true,
              },
            ].map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <div className={`h-full flex flex-col rounded-[var(--radius-glass)] p-7 border ${t.highlight ? 'bg-navy-900 text-white border-navy-900' : 'bg-white border-glass-border'}`}>
                  <h3 className="text-xl font-bold">{t.name}</h3>
                  <div className="mt-4 text-3xl font-extrabold">{t.price}</div>
                  <div className={`text-xs ${t.highlight ? 'text-slate-400' : 'text-text-muted'}`}>{t.sub}</div>
                  <p className={`mt-4 text-sm leading-relaxed flex-1 ${t.highlight ? 'text-slate-300' : 'text-text-secondary'}`}>{t.body}</p>
                  <div className="mt-6">
                    <Button to={t.to} variant={t.highlight ? 'light' : 'secondary'} className="w-full" arrow>
                      {t.cta}
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-6 flex flex-col md:flex-row md:items-center gap-5 rounded-[var(--radius-glass)] border border-glass-border bg-white p-5">
            <img src="/hmi-ink-logo.png" alt="HMI.ink" className="h-12 w-auto self-start md:self-center" />
            <div className="flex-1 text-sm text-text-secondary">
              <b className="text-text-primary">Just need an HMI?</b> HMI.ink connects to your machines, browses tags, and runs dashboards with buttons and custom HTML screens —{' '}
              <b className="text-text-primary">{formatUsd(HMI_LICENSE_PRICE_USD)} per appliance</b>, optional {formatUsd(HMI_SUPPORT_YEARLY_USD)}/year support. Upgrade to Scada.Ink later with the HMI.ink price credited.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button to="/license-builder?package=hmi" size="sm" arrow>
                Buy HMI.ink
              </Button>
              <Button href={HMI_SITE_URL} size="sm" variant="secondary">
                hmi.ink
              </Button>
            </div>
          </div>
          <p className="mt-4 text-xs text-text-muted">
            Optional maintenance adds software updates and priority support for {Math.round(MAINTENANCE_RATE * 100)}% of the license value per year. The base price covers one appliance; expansion cards, accessories and multi-site orders are quoted —{' '}
            <Link to={orderLink('quote')} className="font-semibold text-brand-primary">request a quote</Link>. Already have an appliance? Licensed features can be added to it on their own.
          </p>
          <Link
            to="/partners"
            className="mt-6 flex items-center gap-3 rounded-[var(--radius-glass-sm)] border border-status-running/25 bg-status-running/5 px-4 py-3 text-sm hover:border-status-running/50 transition-colors"
          >
            <Handshake size={18} className="text-status-running shrink-0" />
            <span className="flex-1 text-text-secondary">
              <b className="text-text-primary">Resellers and integrators:</b> certified partners save up to {MAX_PARTNER_DISCOUNT}% on software licenses, applied automatically in the License Builder.
            </span>
            <ArrowRight size={16} className="text-status-running" />
          </Link>
        </Container>
      </section>

      {/* What each tier includes — the one place the site names them. */}
      <section id="tiers" className="scroll-mt-32 pb-20">
        <Container>
          <SectionHeading
            eyebrow="Feature tiers"
            title="What’s included at each level"
            intro="Features are licensed one by one, but most plants land on one of these three. Pick one as a starting point in the License Builder and add or remove from there."
          />
          <div className="grid lg:grid-cols-3 gap-5">
            {SOFTWARE_TIERS.map((t, i) => (
              <Reveal key={t.id} delay={i * 80}>
                <div className={`h-full flex flex-col rounded-[var(--radius-glass)] p-7 border ${i === 1 ? 'bg-navy-900 text-white border-navy-900' : 'bg-white border-glass-border'}`}>
                  <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${i === 1 ? 'text-brand-accent' : 'text-brand-primary'}`}>Tier {i + 1}</div>
                  <h3 className="mt-2 text-2xl font-bold">{t.name}</h3>
                  <p className={`mt-3 text-sm leading-relaxed flex-1 ${i === 1 ? 'text-slate-300' : 'text-text-secondary'}`}>{t.summary}</p>
                  <div className="mt-6">
                    <Button
                      to={i === 0 ? '/license-builder' : `/license-builder?features=${LICENSE_PRESETS[i - 1].features.join(',')}`}
                      variant={i === 1 ? 'light' : 'secondary'}
                      size="sm"
                      arrow
                    >
                      {i === 0 ? `Included in the ${formatUsd(APPLIANCE_BASE_PRICE_USD)} base` : `Price ${t.name}`}
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Cloud */}
      <section id="cloud" className="scroll-mt-32 py-20 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading
            eyebrow="Scada.Ink Cloud"
            title="Monthly cloud plans"
            intro="Mix and match by service, or take everything for one appliance with Cloud Complete. Fleet health monitoring is free for every registered appliance."
          />
          <div className="space-y-16">
            {CLOUD_PLAN_CATEGORIES.map((cat) => {
              const plans = CLOUD_PLANS.filter((p) => p.category === cat.id);
              return (
                <div key={cat.id} id={CATEGORY_ANCHORS[cat.id]} className="scroll-mt-32">
                  <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                    <div>
                      <Eyebrow>{cat.name}</Eyebrow>
                      <p className="mt-2 text-text-secondary">{cat.summary}</p>
                    </div>
                  </div>
                  <div className={`grid gap-5 ${plans.length === 1 ? 'lg:grid-cols-[minmax(0,420px)]' : 'md:grid-cols-2 lg:max-w-4xl'}`}>
                    {plans.map((p) => (
                      <PlanCard key={p.id} plan={p} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex items-start gap-3 rounded-[var(--radius-glass-sm)] bg-ink-muted px-4 py-3 text-xs text-text-secondary">
            <Info size={14} className="mt-0.5 shrink-0 text-text-muted" />
            <span>
              Prices in US dollars, excluding tax. Per-appliance plans are billed for the number of appliances you choose. Unused SMS, voice and AI allowances do not roll over.
              When you bring your own Twilio account, Twilio bills you directly for messages.
            </span>
          </div>
        </Container>
      </section>

      <section className="py-20">
        <Container>
          <SectionHeading eyebrow="Questions" title="Pricing FAQ" />
          <div className="grid md:grid-cols-2 gap-5">
            {[
              ['Do I need a cloud plan to use Scada.Ink?', 'No. Every on-appliance feature works with no internet and no subscription. Cloud plans add services on top.'],
              ['How do the AI features work on an air-gapped appliance?', 'The request is relayed through the browser of the engineer using Smart Assist, which can reach both the appliance and the internet. The appliance itself never connects out.'],
              ['Can I change or cancel a plan?', 'Yes. Change quantities, cancel or update payment details any time from the Billing page of the customer portal.'],
              ['What counts as an appliance for per-appliance plans?', 'Each Scada.Ink appliance registered to your account that you enable the service for. You choose which ones in the portal.'],
              ['What does the base price include?', `${formatUsd(APPLIANCE_BASE_PRICE_USD)} buys a Scada.Ink appliance with the core platform: every PLC, CNC and robot driver, unlimited devices and tags, dashboards, the historian, alarms, scripts and workflows. Paired appliances are included too: link two or more Scada.Ink appliances over your network to share tags and alarms, catch up on history after a network outage, and send script and workflow messages. Licensed features and cloud services are added on top.`],
              ['Is machine vision included?', `Machine vision is its own licensed feature: ${formatUsd(LICENSE_FEATURE_PRICES_USD.machine_vision)} per appliance, one-time and perpetual. It covers every camera on that appliance and every inspection tool, plus millimeter and robot coordinates.`],
              ['Is training included?', 'Ink U is free for everyone. Proctored certification exam vouchers can be added to any order.'],
              ['Do you offer volume or partner pricing?', `Yes. Volume pricing is part of every quote, and certified partners save up to ${MAX_PARTNER_DISCOUNT}% on software.`],
            ].map(([q, a]) => (
              <div key={q} className="glass-card p-6">
                <h3 className="font-semibold">{q}</h3>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}
